import { CoreImageView, CoreTextView } from '@core/widgets';
import { ConditionalRender } from '@libs/core';
import { Glass, type GlassOptics } from '@samasante/liquid-glass';
import clsx from 'clsx';
import React from 'react';
import { SemanticColor, semanticColors } from '../../../theme/colors';
import { TextStyle } from '../../../types/styles';
import { getInlineStyles } from '../../../utils/styles';
import { getHref } from '../../Image/imageUtils';
import { VisualView } from '../../Image/VisualView';
import { Tag } from '../../UI/Tag/Tag';
import styles from './paysendCard.module.css';

const CARD_GLASS_OPTICS: Partial<GlassOptics> = {
    mapSize: 256,
    frost: 14,
    saturate: 1.2,
    strength: 0.03,
    depth: 0.24,
    curvature: 0.6,
    bend: 0.5,
    bendWidth: 0.1,
    dispersion: 0.28,
    sheen: 0.42,
    sheenWidth: 4,
    glow: 0.14,
    brightness: 0.04,
};

type TPaysendCard = {
    isCardScreen?: boolean;
    isFrozen?: boolean;
    isExpired?: boolean;
    isKycInProgress?: boolean;
    appleWalletAdded?: boolean;
    googleWalletAdded?: boolean;
    getCard?: string;
    metaText?: string;
    isFirstRender?: boolean;
    isCardIssued?: boolean;
};

export const PaysendCard = ({
    isCardScreen,
    isFrozen,
    isExpired,
    isKycInProgress,
    appleWalletAdded,
    googleWalletAdded,
    metaText,
    getCard,
    isFirstRender,
    isCardIssued,
}: TPaysendCard) => {
    const isInactive = isFrozen || isExpired || isKycInProgress;
    return (
        <Glass
            className={clsx(
                styles.cardContainer,
                isCardScreen && styles.cardContainerOnCardScreen,
                isCardScreen ? styles.cardAnimationZoomIn : styles.cardAnimationZoomOut,
                isFrozen && styles.frozen,
                isExpired && styles.expired,
                isFirstRender && styles.firstRender,
                isInactive && styles.cardContainerInactive,
            )}
            optics={CARD_GLASS_OPTICS}
            style={{
                display: 'flex',
                ...getInlineStyles({
                    variables: {
                        'card-background-image': `url(${getHref('ic_psCardBalance')})`,
                    },
                }),
            }}
        >
            <div className={styles.cardSurface}>
                <div className={clsx(styles.cardRow, styles.topAlignment, styles.flexEnd)}>
                    {isInactive && (
                        <div className={styles.tagContainer}>
                            <Tag metaVariant={'tag'} metaHierarchy={'inverted'} size={isCardScreen ? 'default' : 'small'} metaText={metaText} />
                        </div>
                    )}
                </div>
                <div className={clsx(styles.cardRow, isCardIssued && styles.flexEnd)}>
                    <ConditionalRender condition={!isCardIssued && !isKycInProgress}>
                        <CoreTextView text={getCard} as="span" styleProps={{ ...TextStyle.main, main: semanticColors[SemanticColor.TextPrimaryOnDarkFill] }} />
                        <VisualView iconName={'ic_arrow_forward'} iconHierarchy={'emphasised'} iconColor={semanticColors[SemanticColor.FillSecondaryOnDark]} />
                    </ConditionalRender>
                    <ConditionalRender condition={isCardIssued}>
                        <div className={clsx(styles.paymentIconsRow, isCardScreen && styles.paymentIconsRowCardScreen)}>
                            {appleWalletAdded && (
                                <div className={styles.iconContainer}>
                                    <CoreImageView
                                        src={getHref('ic_apple_pay_small')}
                                        styleProps={{
                                            width: isCardScreen ? 32 : 21,
                                        }}
                                    />
                                </div>
                            )}
                            {googleWalletAdded && (
                                <div className={styles.iconContainer}>
                                    <CoreImageView
                                        src={getHref('ic_google_pay_small')}
                                        styleProps={{
                                            width: isCardScreen ? 38 : 25,
                                        }}
                                    />
                                </div>
                            )}
                            <div className={styles.iconContainer}>
                                <CoreImageView
                                    src={getHref('mastercard')}
                                    styleProps={{
                                        width: isCardScreen ? 40 : 27,
                                    }}
                                />
                            </div>
                        </div>
                    </ConditionalRender>
                </div>
            </div>
            {(isFrozen || isExpired) && <div className={styles.frozenFrost} aria-hidden={true} />}
        </Glass>
    );
};
